import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, CheckCircle2, Shield } from 'lucide-react';

export default function WalletSecurityChallenge() {
  const [currentScenario, setCurrentScenario] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [answered, setAnswered] = useState(false);

  const scenarios = [
    {
      title: "Phishing Attack",
      scenario: "You receive an email that looks like it's from MetaMask with a link to 'verify your account'. What do you do?",
      answers: [
        { text: "Click the link and verify my account", correct: false, explanation: "DANGEROUS! This is a classic phishing attack. Always go directly to official websites." },
        { text: "Ignore the email and verify through the official MetaMask app/website", correct: true, explanation: "✓ Correct! Never click suspicious links. Always navigate through official channels." },
        { text: "Click the link but don't enter my seed phrase", correct: false, explanation: "Still risky! Phishing sites can steal much more. Always verify authenticity first." },
      ]
    },
    {
      title: "Seed Phrase Security",
      scenario: "Where should you store your seed phrase?",
      answers: [
        { text: "In a note on your computer desktop", correct: false, explanation: "NO! Your computer could be hacked. Seed phrases should be written down and kept physically safe." },
        { text: "In a password manager like LastPass", correct: false, explanation: "While more secure than plain text, it's not ideal. Seed phrases should be kept offline." },
        { text: "Written on paper and stored in a safe location", correct: true, explanation: "✓ Correct! Physical security is best for seed phrases. Consider storing in multiple locations." },
        { text: "Memorized only", correct: false, explanation: "Risky! People forget things. Always have a backup." },
      ]
    },
    {
      title: "Private Key Exposure",
      scenario: "You're using a DApp and it asks permission to access your private key. What's the correct action?",
      answers: [
        { text: "Approve it - the DApp is verified", correct: false, explanation: "NO! A DApp should NEVER ask for your private key directly. A verified DApp still shouldn't have it." },
        { text: "Use a hardware wallet or WalletConnect instead", correct: true, explanation: "✓ Correct! Use WalletConnect or hardware wallets. They never expose your private key." },
        { text: "Approve only if the DApp is popular", correct: false, explanation: "Even popular DApps shouldn't access your private key directly. Always be cautious." },
      ]
    },
    {
      title: "Suspicious Transaction",
      scenario: "You see a pending transaction that you don't remember initiating for an unusual amount. What should you do?",
      answers: [
        { text: "Wait for it to confirm - it might be a reward you forgot about", correct: false, explanation: "NO! If you don't recognize it, act immediately. Cancel if possible or seek help." },
        { text: "Speed up the transaction", correct: false, explanation: "WRONG! You should cancel it or replace it, not speed it up!" },
        { text: "Try to cancel/replace the transaction and disconnect from all DApps", correct: true, explanation: "✓ Correct! Cancel it immediately and revoke all suspicious approvals." },
        { text: "Share the transaction hash on social media for help", correct: false, explanation: "NO! Never share transaction details or ask for help publicly. Scammers will target you." },
      ]
    },
    {
      title: "Network Switching",
      scenario: "A DApp asks you to switch to a network you don't recognize. What's the safest action?",
      answers: [
        { text: "Switch networks - DApps know what networks to use", correct: false, explanation: "DANGER! Switching to fake networks is a common scam. Verify the network chain ID first." },
        { text: "Check the network details and verify it's legitimate before switching", correct: true, explanation: "✓ Correct! Always verify network details. Scammers create fake networks to steal funds." },
        { text: "Switch if the DApp is popular", correct: false, explanation: "Popularity doesn't guarantee legitimacy. Always verify independently." },
      ]
    },
    {
      title: "Token Approval Scam",
      scenario: "You approved unlimited tokens for a DApp. You no longer use it. What should you do?",
      answers: [
        { text: "Nothing - approval doesn't matter if you don't use the DApp", correct: false, explanation: "RISKY! Old approvals can be exploited. You should revoke unused approvals." },
        { text: "Revoke the token approval through a tool like Etherscan or Revoke.cash", correct: true, explanation: "✓ Correct! Always revoke old approvals. This prevents potential exploits." },
        { text: "Send your funds to a new wallet", correct: false, explanation: "Not necessary if you revoke the approval. Just revoke it to be safe." },
      ]
    },
    {
      title: "Gas Price Manipulation",
      scenario: "The gas price suddenly shows as extremely high. What's likely happening?",
      answers: [
        { text: "The blockchain is overloaded - pay the high gas", correct: false, explanation: "Sometimes yes, but check first! High gas can also be a sign of a scam contract." },
        { text: "Check if you're interacting with a potentially malicious contract that's draining gas", correct: true, explanation: "✓ Correct! Unusually high gas estimates can indicate a scam. Always verify contracts first." },
        { text: "Cancel the transaction and restart your wallet", correct: false, explanation: "While harmless, it doesn't address the real issue - verify the contract." },
      ]
    },
    {
      title: "Hardware Wallet Best Practices",
      scenario: "You're setting up a hardware wallet. What should you do?",
      answers: [
        { text: "Skip backing up the seed phrase - hardware wallets don't need backups", correct: false, explanation: "WRONG! Hardware wallets still need seed phrase backups in case the device is lost." },
        { text: "Take a photo of the seed phrase to store in cloud", correct: false, explanation: "NO! Never store seed phrases digitally. Always use physical storage only." },
        { text: "Write the seed phrase on paper, store copies in safe locations, and backup properly", correct: true, explanation: "✓ Correct! Proper backup procedures are critical for security and recovery." },
      ]
    }
  ];

  const scenario = scenarios[currentScenario];

  const submitAnswer = (answer) => {
    setSelectedAnswer(answer);
    setAnswered(true);

    if (answer.correct) {
      setScore(score + 1);
      setFeedback(`✓ ${answer.explanation}`);
    } else {
      setFeedback(`✗ ${answer.explanation}`);
    }

    setTimeout(() => {
      if (currentScenario < scenarios.length - 1) {
        setCurrentScenario(currentScenario + 1);
        setSelectedAnswer(null);
        setFeedback('');
        setAnswered(false);
      } else {
        setCompleted(true);
      }
    }, 3000);
  };

  if (completed) {
    const percentage = (score / scenarios.length) * 100;
    let level = '';
    if (percentage >= 87) level = '🔒 Security Expert';
    else if (percentage >= 75) level = '🛡️ Security Conscious';
    else if (percentage >= 60) level = '⚠️ Security Aware';
    else level = '📚 Keep Learning';

    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="text-5xl mb-4">🔐</div>
          <h2 className="text-2xl font-bold mb-2 text-white">Challenge Complete!</h2>
          <p className="text-4xl font-bold text-green-400 mb-2">{score}/{scenarios.length}</p>
          <Badge className="mb-4 text-lg py-2">{level}</Badge>
          <p className="text-gray-300 mb-6">
            You scored {percentage.toFixed(0)}% on wallet security!
          </p>
          <Button onClick={() => window.location.href = '/games'} className="w-full">
            Back to Games
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">🔐 Wallet Security Challenge</h1>
          <div className="flex justify-between items-center">
            <p className="text-gray-300">Scenario {currentScenario + 1}/{scenarios.length}</p>
            <Badge className="bg-green-500">{score} Correct</Badge>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-slate-700 rounded-full h-2 mb-8">
          <div
            className="bg-green-500 h-2 rounded-full transition-all"
            style={{ width: `${((currentScenario + 1) / scenarios.length) * 100}%` }}
          />
        </div>

        {/* Scenario Card */}
        <Card className="p-8 bg-slate-800 border-slate-700 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">{scenario.title}</h2>
          <p className="text-gray-200 text-lg mb-8">{scenario.scenario}</p>

          {/* Answers */}
          <div className="space-y-3 mb-6">
            {scenario.answers.map((answer, index) => (
              <button
                key={index}
                onClick={() => !answered && submitAnswer(answer)}
                disabled={answered}
                className={`w-full p-4 rounded text-left transition ${
                  answered
                    ? answer.correct
                      ? 'bg-green-900 border-2 border-green-600'
                      : selectedAnswer === answer
                      ? 'bg-red-900 border-2 border-red-600'
                      : 'bg-slate-700 border-2 border-slate-600'
                    : 'bg-slate-700 border-2 border-slate-600 hover:border-slate-500 cursor-pointer'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1 font-bold text-lg">
                    {answered && answer.correct && '✓'}
                    {answered && !answer.correct && selectedAnswer === answer && '✗'}
                    {!answered && '○'}
                  </div>
                  <p className="text-white">{answer.text}</p>
                </div>
              </button>
            ))}
          </div>

          {/* Feedback */}
          {feedback && (
            <Card className={`p-4 ${feedback.startsWith('✓') ? 'bg-green-900 border-green-700' : 'bg-red-900 border-red-700'}`}>
              <p className={feedback.startsWith('✓') ? 'text-green-200' : 'text-red-200'}>
                {feedback}
              </p>
            </Card>
          )}
        </Card>

        {/* Security Tips */}
        <Card className="p-6 bg-slate-700 border-slate-600">
          <h3 className="text-lg font-bold text-white mb-3">🛡️ Security Tips</h3>
          <ul className="space-y-2 text-gray-300 text-sm">
            <li>✓ Never share your seed phrase with anyone</li>
            <li>✓ Always verify URLs before entering sensitive info</li>
            <li>✓ Use hardware wallets for large amounts</li>
            <li>✓ Revoke old token approvals regularly</li>
            <li>✓ Enable 2FA on all accounts</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
